import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NetworkProfilesApi, type NetworkProfile } from '@/services/networks/networksApi'
import { useNetwork, NETWORK_STORAGE_KEY } from './NetworkContext'

const CACHE_PREFIX = 'adnuntius.rui.networkFieldSettings.'

function readCache(tagId?: string): NetworkProfile | undefined {
  if (!tagId) return undefined
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + tagId)
    return raw ? (JSON.parse(raw) as NetworkProfile) : undefined
  } catch {
    return undefined
  }
}

function writeCache(tagId: string, profile: NetworkProfile) {
  try {
    localStorage.setItem(CACHE_PREFIX + tagId, JSON.stringify(profile))
  } catch {
    /* private mode / quota — the settings just aren't pre-seeded next time */
  }
}

interface NetworkProfileContextValue {
  /**
   * The current network's profile — its field defaults and logo. Right after a
   * reload this can be the copy the last visit left in localStorage, so forms
   * lay out correctly on first paint; it is replaced once the request lands.
   */
  networkProfile: NetworkProfile | undefined
  /** True once the profile has come from the server, not the stored copy. */
  isFresh: boolean
  isLoading: boolean
  isError: boolean
  /** Shares a freshly saved profile, so everything shows it at once. */
  setNetworkProfile: (profile: NetworkProfile) => void
}

const NetworkProfileContext = createContext<NetworkProfileContextValue | null>(null)

/** The current network's profile, loaded once and shared. */
export function NetworkProfileProvider({ children }: { children: ReactNode }) {
  const { currentNetwork } = useNetwork()
  const queryClient = useQueryClient()
  const tagId = currentNetwork?.tagId
  // Right after a reload the network itself is still loading, so fall back to
  // the tag the shell last used to find the stored copy.
  const cacheTagId = tagId ?? localStorage.getItem(NETWORK_STORAGE_KEY) ?? undefined

  const { data, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: ['networkProfile', tagId],
    queryFn: async () => {
      const fetched = await NetworkProfilesApi.get(tagId!)
      writeCache(tagId!, fetched)
      return fetched
    },
    enabled: !!tagId,
    placeholderData: () => readCache(cacheTagId),
    staleTime: 5 * 60 * 1000,
  })

  const setNetworkProfile = useCallback(
    (profile: NetworkProfile) => {
      if (!tagId) return
      writeCache(tagId, profile)
      queryClient.setQueryData(['networkProfile', tagId], profile)
    },
    [queryClient, tagId]
  )

  const value = useMemo<NetworkProfileContextValue>(
    () => ({
      networkProfile: data,
      isFresh: !!data && !isPlaceholderData,
      isLoading,
      isError,
      setNetworkProfile,
    }),
    [data, isPlaceholderData, isLoading, isError, setNetworkProfile]
  )

  return <NetworkProfileContext.Provider value={value}>{children}</NetworkProfileContext.Provider>
}

export function useNetworkProfile() {
  const context = useContext(NetworkProfileContext)
  if (!context) throw new Error('useNetworkProfile must be used inside NetworkProfileProvider')
  return context
}
